
import { FactcheckSuggestion } from "@/types";
import { Component, Vue, Watch } from "vue-property-decorator";
import { mixins } from "vue-class-component";
import * as FactcheckRequestServices from "@/services/FactCheckRequestServices";
import ConfirmationDialog from "@/components/dialogs/ConfirmationDialog.vue";
import { useDebounceFn } from '@vueuse/core'
import { format, intervalToDuration } from "date-fns";
import { TipTap } from '@/mixins/TipTap';
import { ROOT_ACTIONS } from "@/store/actions";
import { safeHtml } from '@/sanitize';
import { csvCell } from '@/csv';
import { askConfirm } from '@/content/confirm';


@Component({
  components: {
    ConfirmationDialog,
    NewFactcheckSuggestionDialog: () => import('@/components/dialogs/NewFactcheckSuggestion.vue'),
  },
})
export default class Index extends mixins(TipTap) {
  safeHtml = safeHtml
  tab = 0
  headers = [
    {
      text: "Aanvraag",
      value: "claim",
      cellClass: 'cp',
    },
    {
      text: 'Bron',
      value: 'source',
      cellClass: '',
    },
    {
      text: "Datum",
      value: "date",
      width: 150,
      cellClass: 'cp text-no-wrap',
    },
    {
      text: "Email",
      value: "email",
      cellClass: 'cp',
    },
    {
      text: 'WhatsApp',
      value: 'whatsappCode',
      cellClass: 'cp',
    },
    {
      text: "Ontvangen",
      value: "receivedAt",
      width: 150,
      cellClass: 'cp text-no-wrap',
    },
    {
      text: "Antwoord",
      value: "answered",
    },
    {
      text: "Reactietijd",
      value: "responseTime",
      width: 120,
      cellClass: 'cp text-no-wrap',
    },
    {
      text: "Beantwoord op",
      value: "respondedAt",
      width: 150,
      cellClass: 'cp text-no-wrap',
    },
    {
      text: "Doorgegeven",
      value: "passed",
    },
    {
      text: 'Zelf opgelost',
      value: 'solvedByDeCheckers',
    },
    {
      text: "Verwijderen",
      value: "id",
    },
  ];



  requests: FactcheckSuggestion[] = [];

  search = '';
  loadingRequests = false;

  selectedRequest: FactcheckSuggestion | null = null;

  response = ``;
  applySignature = true;

  dialogs = {
    details: false,
    sendResponse: false,
    newSuggestion: false,
  };

  loading = {
    sendResponse: false,
  }

  factchecks: Array<{ id: string, title: string }> = [];
  percentageOfRequestsWithFactcheck = null as number | null;
  requestsWithFactcheck = 0;
  async mounted() {
    await this.getRequests();
    const statData = await FactcheckRequestServices.GetPercentageOfRequestWithFactCheck();
    this.percentageOfRequestsWithFactcheck = statData.percentage
    this.requestsWithFactcheck = statData.countWithFactcheck
  }

  async getRequests() {
    this.loadingRequests = true;
    try {
      this.requests = await FactcheckRequestServices.GetRequests(this.search.trim() || undefined);
    } finally {
      this.loadingRequests = false;
    }
  }

  debouncedSearch = useDebounceFn(async () => {
    await this.getRequests();
  }, 300)

  @Watch('search')
  onSearchChange() {
    this.debouncedSearch();
  }

  debouncedUpdate = useDebounceFn(async () => {
    await this.updateSelectedRequest();
  }, 500)


  openRequest(request: FactcheckSuggestion) {
    this.selectedRequest = request;
    this.dialogs.details = true;
  }

  async prefillTemplate() {
    let template = localStorage.getItem('requestTemplate') || '';

    template = template.replaceAll('[[naam]]', this.selectedRequest!.fullname)

    this.response = template;
  }

  async deleteRequest(id: string) {
    if (!await askConfirm({ title: 'Aanvraag verwijderen?', text: 'De aanvraag wordt meteen verwijderd en kan niet teruggezet worden.', confirmLabel: 'Verwijderen', danger: true })) return;
    await FactcheckRequestServices.DeleteRequest(id);
    await this.getRequests();
  }

  async updateSelectedRequest() {
    if (this.selectedRequest == null) return;
    if (this.selectedRequest.FactcheckId && this.selectedRequest.FactcheckId.includes('decheckers.be')) {
      // get the id https://decheckers.be/factchecks/factcheck-krijgen-huishoudhulpen-een-jobbonus-van-450-tot-500-euro-zoals-hilde-crevits-cdv-zegt-in-de-zevende-dag
      this.selectedRequest.FactcheckId = this.selectedRequest.FactcheckId.split('/').pop() || '';
    }
    if (this.selectedRequest.FactcheckId == '') {
      this.selectedRequest.FactcheckId = null;
    }
    await FactcheckRequestServices.UpdateRequest(this.selectedRequest.id, this.selectedRequest!);
    this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
      color: 'info',
      text: 'Aanvraag bijgewerkt'
    })
  }

  openImage() {
    window.open(`https://cdn.decheckers.be/requests/${this.selectedRequest!.id}`)
  }

  formatDate(date: string) {
    const d = new Date(date);
    const isUtcMidnight =
      d.getUTCHours() === 0 &&
      d.getUTCMinutes() === 0 &&
      d.getUTCSeconds() === 0 &&
      d.getUTCMilliseconds() === 0;
    return format(d, isUtcMidnight ? "dd-MM-yyyy" : "dd-MM-yyyy HH:mm");
  }

  async sendResponse() {

    this.loading.sendResponse = true;
    await FactcheckRequestServices.SendResponse(this.selectedRequest!.id, this.response, this.applySignature);
    this.loading.sendResponse = false;
    this.dialogs.sendResponse = false;
    this.response = '';
    await this.getRequests();
    this.selectedRequest = this.requests.find(r => r.id == this.selectedRequest!.id) || null;
  }

  /** The tip form's email is optional; without it there is nobody to reply to. */
  get hasEmail(): boolean {
    return Boolean(this.selectedRequest?.email?.trim());
  }

  get uniqueSources() {
    return [...new Set(this.requests.map(r => r.source))];
  }

  private toLocalDatetimeString(iso: string | Date | null | undefined): string {
    if (!iso) return '';
    const date = new Date(iso);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  get formattedDate(): string {
    return this.toLocalDatetimeString(this.selectedRequest?.date);
  }

  set formattedDate(value: string) {
    if (!this.selectedRequest) return;
    this.selectedRequest.date = new Date(value).toISOString();
  }

  get formattedReceivedAt(): string {
    return this.toLocalDatetimeString(this.selectedRequest?.receivedAt);
  }

  set formattedReceivedAt(value: string) {
    if (!this.selectedRequest) return;
    this.selectedRequest.receivedAt = value ? new Date(value).toISOString() : '';
  }

  get formattedRespondedAt(): string {
    return this.toLocalDatetimeString(this.selectedRequest?.respondedAt);
  }

  set formattedRespondedAt(value: string) {
    if (!this.selectedRequest) return;
    this.selectedRequest.respondedAt = value ? new Date(value).toISOString() : null;
  }

  onTimestampFocus(field: 'receivedAt' | 'respondedAt') {
    if (!this.selectedRequest) return;
    if (!this.selectedRequest[field]) {
      (this.selectedRequest as any)[field] = new Date().toISOString();
    }
  }

  onAnsweredChange() {
    if (this.selectedRequest?.answered && !this.selectedRequest.respondedAt) {
      this.selectedRequest.respondedAt = new Date().toISOString();
    }
    this.updateSelectedRequest();
  }

  formatResponseTime(receivedAt: string | null, respondedAt: string | null): string {
    if (!receivedAt || !respondedAt) return '—';
    const start = new Date(receivedAt);
    const end = new Date(respondedAt);
    if (end <= start) return '—';
    const duration = intervalToDuration({ start, end });
    const parts: string[] = [];
    if (duration.years) parts.push(`${duration.years}j`);
    if (duration.months) parts.push(`${duration.months}ma`);
    if (duration.days) parts.push(`${duration.days}d`);
    if (duration.hours) parts.push(`${duration.hours}u`);
    return parts.length ? parts.join(' ') : '< 1u';
  }

  convertToCSV(objArray: any[]) {
    const array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray;
    let str = '';
    const header = Object.keys(array[0]).filter(key => key !== 'FactcheckSuggestionReplies').join(',');
    str += header + '\r\n';

    for (let i = 0; i < array.length; i++) {
      let line = '';
      for (let index in array[i]) {
        if (index !== 'FactcheckSuggestionReplies') {
          if (line !== '') line += ',';
          line += csvCell(array[i][index]);
        }
      }
      str += line + '\r\n';
    }
    return str;
  }

  downloadCSV(csv: any, filename: string) {
    const csvFile = new Blob([csv], { type: 'text/csv' });
    const downloadLink = document.createElement('a');
    downloadLink.download = filename;
    downloadLink.href = window.URL.createObjectURL(csvFile);
    downloadLink.style.display = 'none';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }

  loadingExport = false;
  async exportRequests() {
    this.loadingExport = true;
    const csv = this.convertToCSV(this.requests);
    this.downloadCSV(csv, 'requests.csv');
    this.loadingExport = false;
  }
}
